import { useState, useRef, useCallback, useEffect } from "react";
import { ElevenLabsTTSProvider, StreamingTTSProvider, type TTSProvider } from "./TTSProvider";
import { SentenceBuffer } from "./SentenceBuffer";
import { useBargeIn } from "./useBargeIn";
import { isNative } from "@/lib/capacitor";
// Audio cues disabled — user prefers visual-only feedback via orb animation
// import { warmAudio, playConnectCue, playDisconnectCue, startProcessingCueLoop, stopProcessingCueLoop, disposeCueContext } from "./audio-cues";
import { createSpeechInputProvider, type SpeechInputProvider } from "./SpeechInputProvider";
import { cleanForSpeech } from "./speech-text";

export type VoiceModePhase = "idle" | "listening" | "processing" | "speaking";

interface UseVoiceModeOptions {
  /** Called when user speech is finalized and should be sent to the agent */
  onSend: (text: string) => void;
  /** Called when transcript is finalized, before sending — use to show text in input */
  onTranscriptReady?: (text: string) => void;
  /** Called after message is sent — use to clear input */
  onTranscriptSent?: () => void;
  /** Called on barge-in to abort the active SSE stream */
  onAbortAgent?: () => void;
  /** Silence timeout in ms after last isFinal result before auto-sending */
  silenceTimeout?: number;
  /** Auth token for TTS proxy */
  token?: string | null;
  /** User's first name for greeting on enter */
  userName?: string;
}

interface UseVoiceModeReturn {
  isActive: boolean;
  phase: VoiceModePhase;
  /** True when the mic is hot and accepting user speech (phase === "listening") */
  isMicLive: boolean;
  userTranscript: string;
  interimTranscript: string;
  assistantTranscript: string;
  isSpeaking: boolean;
  isDucking: boolean;
  enter: () => Promise<void>;
  exit: () => void;
  /** Toggle mic between live (listening) and muted. From speaking/processing,
   *  acts as a manual stop (delegates to stopSpeaking) — distinct from barge-in
   *  semantics: no "interrupted" flag on the next turn. */
  toggleMic: () => void;
  speakResponse: (text: string) => void;
  /** Feed a streaming text chunk into the sentence buffer → TTS pipeline */
  feedTextChunk: (text: string) => void;
  /** Signal that streaming is done — flush remaining text and finalize TTS turn */
  finishSpeaking: () => void;
  /** Explicit user stop while the agent is talking — halts TTS, drops to
   *  listening. Differs from exit() (which leaves voice mode entirely) and
   *  from barge-in (which marks the next turn as interrupted). */
  stopSpeaking: () => void;
  /** True if streaming TTS failed and onMessage should fall back to speakResponse */
  streamingTtsFailed: boolean;
  signalProcessing: (immediate?: boolean) => void;
  getOutputVolume: () => number;
}

export function useVoiceMode(options: UseVoiceModeOptions): UseVoiceModeReturn {
  // 1500ms default: long enough that natural mid-sentence pauses ("uhh…",
  // breath, thinking) don't cause premature send, short enough that finished
  // sentences don't feel laggy. 800ms (the original default) was clipping
  // users mid-thought; 1300ms still occasionally fired between phrases.
  const { onSend, onTranscriptReady, onTranscriptSent, onAbortAgent, silenceTimeout = 1500, token, userName } = options;
  const onTranscriptReadyRef = useRef(onTranscriptReady);
  onTranscriptReadyRef.current = onTranscriptReady;
  const onTranscriptSentRef = useRef(onTranscriptSent);
  onTranscriptSentRef.current = onTranscriptSent;
  const onAbortAgentRef = useRef(onAbortAgent);
  onAbortAgentRef.current = onAbortAgent;

  const [isActive, setIsActive] = useState(false);
  const [phase, setPhase] = useState<VoiceModePhase>("idle");
  const [userTranscript, setUserTranscript] = useState("");
  const [interimTranscript, setInterimTranscript] = useState("");
  const [assistantTranscript, setAssistantTranscript] = useState("");
  const [isDucking, setIsDucking] = useState(false);
  // Manual mic mute is orthogonal to phase — agent state, TTS, and orb
  // animation are unaffected. The user's mic is simply gated when this is
  // true. Mute persists across turns until the user toggles it back.
  const [userMuted, setUserMuted] = useState(false);
  const userMutedRef = useRef(false);

  const ttsRef = useRef<TTSProvider | null>(null);
  const recognitionRef = useRef<any>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const accumulatedTextRef = useRef("");
  const isActiveRef = useRef(false);
  const phaseRef = useRef<VoiceModePhase>("idle");
  const onSendRef = useRef(onSend);
  onSendRef.current = onSend;

  const speechInputRef = useRef<SpeechInputProvider | null>(null);

  // Throttle SpeechRecognition restarts — browsers kill rapid start/stop cycles
  const recognitionRestartCountRef = useRef(0);
  const recognitionRestartWindowRef = useRef(0);

  // Safety timeout: if phase stays "processing" for too long (backend died, network dropped),
  // recover to listening so the user isn't stuck forever. 3 minutes accommodates long agent
  // tasks like generating full HTML pages or executing multiple tools.
  const PROCESSING_TIMEOUT_MS = 180000;
  const processingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Monotonic token so stale speakResponse() completions cannot mutate phase
  // after a later response interrupts them.
  const speakTokenRef = useRef(0);

  // Hard gate: set to false on barge-in, blocks ALL new speakResponse() calls
  // until the next user send starts. Prevents late SSE callbacks from speaking
  // over the user after they interrupted.
  const acceptAssistantSpeechRef = useRef(true);

  // Safety net: reopen gate after 8s if user barges in but doesn't speak.
  // Primary re-open is in the silence timer send path. 8s is long enough
  // for the aborted SSE stream to fully drain, preventing stale responses
  // from sneaking through the reopened gate.
  const GATE_RECOVERY_MS = 8000;
  const gateRecoveryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Tracks whether the previous turn was interrupted by barge-in.
  // When true, the next onSend prepends context so the LLM knows.
  const wasInterruptedRef = useRef(false);

  // Streaming TTS pipeline
  const streamingTtsRef = useRef<StreamingTTSProvider | null>(null);
  const sentenceBufferRef = useRef<SentenceBuffer | null>(null);
  const currentTurnIdRef = useRef(0);
  const [streamingTtsFailed, setStreamingTtsFailed] = useState(false);

  // Create TTS provider once (not on every token change)
  useEffect(() => {
    const provider = new ElevenLabsTTSProvider();
    ttsRef.current = provider;

    // StreamingTTSProvider shares AudioContext via the parent provider
    const streaming = new StreamingTTSProvider(provider);
    streamingTtsRef.current = streaming;

    return () => {
      try { streaming.dispose(); } catch (_) { /* ignore */ }
      streamingTtsRef.current = null;
      try { provider.dispose(); } catch (_) { /* swallow dispose errors */ }
      if (ttsRef.current === provider) {
        ttsRef.current = null;
      }
    };
  }, []);

  // Update token without recreating the provider
  useEffect(() => {
    if (ttsRef.current && 'setToken' in ttsRef.current) {
      (ttsRef.current as ElevenLabsTTSProvider).setToken(token ?? null);
    }
  }, [token]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) { clearTimeout(silenceTimerRef.current); silenceTimerRef.current = null; }
  }, []);

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    try {
      recognitionRef.current?.stop();
    } catch (_) {
      /* ignore */
    }
  }, [clearSilenceTimer]);

  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      const SR =
        (window as any).SpeechRecognition ||
        (window as any).webkitSpeechRecognition;
      if (!SR) return;
      recognitionRef.current = new SR();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;
      recognitionRef.current.lang = "en-US";
    }

    const recognition = recognitionRef.current;
    accumulatedTextRef.current = "";
    setUserTranscript("");
    setInterimTranscript("");

    recognition.onresult = (event: any) => {
      // Once we've transitioned out of "listening" (silence timer fired,
      // sent the message, recognition is stopping), discard any buffered
      // finals that SpeechRecognition is still flushing. Without this gate
      // those late finals re-arm the silence timer and cause a double-send
      // (turn N's text + any continuation gets sent again as turn N+1
      // while the agent is responding to turn N).
      if (phaseRef.current !== "listening") return;
      // User is manually muted — treat the same way: drop everything.
      // Defensive (we also stopListening() in toggleMic, but late finals
      // can still arrive in the same tick).
      if (userMutedRef.current) return;

      let final = "";
      let interim = "";

      // Start from event.resultIndex, not 0 — with continuous=true,
      // event.results accumulates ALL results from the session.
      // Starting from 0 would re-process old results and cause duplicates.
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (result.isFinal) {
          final += result[0].transcript;
        } else {
          interim += result[0].transcript;
        }
      }

      if (final) accumulatedTextRef.current += final;
      // Expose the full accumulated transcript (not just the latest fragment)
      // so consumers can render it as growing text in the input. Sending
      // still uses accumulatedTextRef.current directly in the silence timer.
      setUserTranscript(accumulatedTextRef.current);
      setInterimTranscript(interim);

      // Reset silence timer on each isFinal result
      if (final) {
        clearSilenceTimer();
        silenceTimerRef.current = setTimeout(() => {
          if (!isActiveRef.current) return;
          const text = accumulatedTextRef.current.trim();
          if (!text) return;

          onTranscriptReadyRef.current?.(text);

          // Re-open the gate for the new turn's responses
          acceptAssistantSpeechRef.current = true;

          phaseRef.current = "processing";
          setPhase("processing");

          stopListening();

          // If previous turn was interrupted, prepend context for the LLM
          const sendText = wasInterruptedRef.current
            ? `[System: The user interrupted your previous spoken reply. Treat this utterance as the highest-priority instruction. Continue the prior task only if this clearly extends it.]\n\n${text}`
            : text;
          wasInterruptedRef.current = false;

          onSendRef.current(sendText);
          onTranscriptSentRef.current?.();

          // Defense in depth against double-send: clear the accumulator
          // before any late SpeechRecognition finals can reach onresult.
          // The phase gate above is the primary defense; this is a backstop.
          accumulatedTextRef.current = "";

          // Arm processing timeout — if backend dies and speakResponse never fires,
          // recover to listening after 60s so the user isn't stuck forever.
          if (processingTimerRef.current) clearTimeout(processingTimerRef.current);
          processingTimerRef.current = setTimeout(() => {
            processingTimerRef.current = null;
            if (!isActiveRef.current) return;
            if (phaseRef.current !== "processing") return; // already moved on
            console.warn("[VoiceMode] Processing timeout — recovering to listening");
            startListening();
          }, PROCESSING_TIMEOUT_MS);
        }, silenceTimeout);
      }
    };

    recognition.onerror = (e: any) => {
      if (e.error !== "no-speech" && e.error !== "aborted") {
        console.error("[VoiceMode] STT error:", e.error);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still in listening phase, with throttling to prevent
      // browsers from killing rapid start/stop cycles (max 5 restarts per 10s window)
      if (isActiveRef.current && phaseRef.current === "listening") {
        const now = Date.now();
        if (now - recognitionRestartWindowRef.current > 10000) {
          recognitionRestartCountRef.current = 0;
          recognitionRestartWindowRef.current = now;
        }
        recognitionRestartCountRef.current++;
        if (recognitionRestartCountRef.current > 5) {
          console.warn("[VoiceMode] Too many STT restarts — backing off 2s");
          setTimeout(() => {
            if (isActiveRef.current && phaseRef.current === "listening") {
              recognitionRestartCountRef.current = 0;
              try { recognition.start(); } catch (_) { /* ignore */ }
            }
          }, 2000);
          return;
        }
        try {
          recognition.start();
        } catch (_) {
          /* ignore */
        }
      }
    };

    phaseRef.current = "listening";
    setPhase("listening");
    try {
      recognition.start();
    } catch (_) {
      /* ignore */
    }
  }, [clearSilenceTimer, silenceTimeout, stopListening]);

  const enter = useCallback(async () => {
    if (isActiveRef.current) return;

    if (!speechInputRef.current) {
      speechInputRef.current = createSpeechInputProvider();
    }
    const speechInput = speechInputRef.current;

    const available = await speechInput.isAvailable();
    if (!available) {
      console.warn("[VoiceMode] Speech recognition not available on this platform");
      return;
    }

    try {
      await speechInput.ensurePermissions();
    } catch (err) {
      console.warn("[VoiceMode] Permission denied:", err);
      return;
    }

    isActiveRef.current = true;
    setIsActive(true);
    speakTokenRef.current += 1;
    acceptAssistantSpeechRef.current = true;
    wasInterruptedRef.current = false;
    userMutedRef.current = false;
    setUserMuted(false);

    await ttsRef.current?.prepare?.();

    // Warm up speechSynthesis on user gesture (browsers block autoplay otherwise)
    try { window.speechSynthesis?.cancel(); } catch (_) { /* ignore */ }

    // Speak greeting, then start listening
    const firstName = userName?.split(/\s+/)[0] || "";
    const greeting = firstName ? `Hello ${firstName}` : "Hello";

    setTimeout(() => {
      if (!isActiveRef.current) return;

      if (ttsRef.current) {
        phaseRef.current = "speaking";
        setPhase("speaking");
        console.log("[VoiceMode] Speaking greeting:", greeting);
        ttsRef.current.speak(greeting).then(() => {
          if (!isActiveRef.current) return;
          if (phaseRef.current === "listening") return; // barge-in already transitioned
          startListening();
        }).catch((err) => {
          console.warn("[VoiceMode] Greeting TTS failed:", err);
          if (!isActiveRef.current) return;
          if (phaseRef.current === "listening") return;
          startListening();
        });
      } else {
        startListening();
      }
    }, 500);
  }, [startListening, userName]);

  const exit = useCallback(() => {
    isActiveRef.current = false;
    setIsActive(false);
    phaseRef.current = "idle";
    setPhase("idle");
    setUserTranscript("");
    setInterimTranscript("");
    setAssistantTranscript("");
    setIsDucking(false);
    userMutedRef.current = false;
    setUserMuted(false);
    if (gateRecoveryTimerRef.current) { clearTimeout(gateRecoveryTimerRef.current); gateRecoveryTimerRef.current = null; }
    if (processingTimerRef.current) { clearTimeout(processingTimerRef.current); processingTimerRef.current = null; }
    speakTokenRef.current += 1;
    acceptAssistantSpeechRef.current = true;
    wasInterruptedRef.current = false;
    stopListening();
    sentenceBufferRef.current?.clear();
    sentenceBufferRef.current = null;
    streamingTtsRef.current?.cancelAll();
    ttsRef.current?.stop();
    clearSilenceTimer();
  }, [clearSilenceTimer, stopListening]);

  const speakResponse = useCallback(
    async (text: string) => {
      // Clear processing timeout — response arrived, no longer at risk of being stuck
      if (processingTimerRef.current) { clearTimeout(processingTimerRef.current); processingTimerRef.current = null; }

      if (!isActiveRef.current || !ttsRef.current) return;
      if (!acceptAssistantSpeechRef.current) {
        console.warn("[VoiceMode] speakResponse blocked — gate closed after barge-in");
        return;
      }

      const speakToken = ++speakTokenRef.current;

      phaseRef.current = "processing";
      setPhase("processing");
      stopListening();

      const spokenText = cleanForSpeech(text);
      setAssistantTranscript(spokenText);

      const tts = ttsRef.current;
      tts.onStart = () => {
        if (!isActiveRef.current || speakTokenRef.current !== speakToken) return;
        phaseRef.current = "speaking";
        setPhase("speaking");
      };
      tts.onEnd = () => {};

      try {
        await tts.speak(spokenText);
      } catch (err) {
        console.error("[VoiceMode] TTS error:", err);
        // TTS failed — fall through to startListening() below so voice mode
        // doesn't get stuck in "processing" or "speaking" phase forever.
      }

      if (!isActiveRef.current) return;
      if (speakTokenRef.current !== speakToken) return;

      setUserTranscript("");
      setInterimTranscript("");
      setAssistantTranscript("");

      startListening();
    },
    [startListening, stopListening],
  );

  const signalProcessing = useCallback((_immediate = false) => {
    if (!isActiveRef.current) return;
    if (phaseRef.current === "speaking") return;
    // Block after barge-in — late tool activity should not start new turns
    if (!acceptAssistantSpeechRef.current) return;
    // Don't restart if a streaming turn is already in progress
    const streaming = streamingTtsRef.current;
    if (streaming?.isTurnActive()) return;

    phaseRef.current = "processing";
    setPhase("processing");

    // Start a new streaming TTS turn so sentences can be queued
    if (streaming) {
      const turnId = streaming.startTurn();
      currentTurnIdRef.current = turnId;
      setStreamingTtsFailed(false);

      streaming.onFirstAudioStart = () => {
        if (currentTurnIdRef.current !== turnId) return;
        if (!isActiveRef.current) return;
        phaseRef.current = "speaking";
        setPhase("speaking");
      };
      streaming.onAudioPause = () => {
        if (currentTurnIdRef.current !== turnId) return;
        if (!isActiveRef.current) return;
        // Preamble audio finished, awaiting tool result / more text.
        // Show the thinking animation instead of the speaking one.
        phaseRef.current = "processing";
        setPhase("processing");
      };
      streaming.onAudioResume = () => {
        if (currentTurnIdRef.current !== turnId) return;
        if (!isActiveRef.current) return;
        phaseRef.current = "speaking";
        setPhase("speaking");
      };
      streaming.onAllDone = () => {
        if (currentTurnIdRef.current !== turnId) return;
        if (!isActiveRef.current) return;
        setAssistantTranscript("");
        startListening();
      };
      streaming.onStreamingFailed = () => {
        setStreamingTtsFailed(true);
      };
    }
  }, [startListening]);

  // --- Streaming TTS: feed text chunks from SSE into sentence buffer ---

  const feedTextChunk = useCallback((text: string) => {
    if (!isActiveRef.current || !acceptAssistantSpeechRef.current) return;

    // Auto-start a streaming TTS turn if none is active.
    // signalProcessing is only called from onToolActivity, so text-only
    // responses (no tool calls) would never start a turn without this.
    const streaming = streamingTtsRef.current;
    if (streaming && !streaming.isTurnActive()) {
      signalProcessing();
    }

    // Clear processing timeout — response text is arriving
    if (processingTimerRef.current) {
      clearTimeout(processingTimerRef.current);
      processingTimerRef.current = null;
    }

    // Lazily create SentenceBuffer for this turn.
    // Read streamingTtsRef inside the callback (not captured in closure)
    // to avoid stale references after HMR/remount.
    if (!sentenceBufferRef.current) {
      sentenceBufferRef.current = new SentenceBuffer((sentence) => {
        const cleaned = cleanForSpeech(sentence);
        if (!cleaned.trim()) return;
        const s = streamingTtsRef.current;
        if (s) s.addSentence(cleaned, currentTurnIdRef.current);
      });
    }

    sentenceBufferRef.current.feed(text);
  }, [signalProcessing]);

  const finishSpeaking = useCallback(() => {
    if (!isActiveRef.current) return;

    // Flush remaining text in the sentence buffer
    sentenceBufferRef.current?.flush();
    sentenceBufferRef.current = null;

    // Only signal finishTurn if a streaming turn is actually active.
    // Prevents spurious onAllDone → startListening if called without a turn
    // (e.g., double-call, or called when no streaming was started).
    const streaming = streamingTtsRef.current;
    if (streaming?.isTurnActive()) {
      streaming.finishTurn();
    }
  }, []);

  // Real-time volume from TTS audio output (0-1)
  // Uses time-domain RMS with attack/release envelope and power-law curve
  // Falls back to synthetic speech cadence when no AnalyserNode (WebSpeech)
  const timeDomainData = useRef(new Float32Array(512));
  const smoothedVolumeRef = useRef(0);
  const peakRef = useRef(0.05);
  const lastVolumeTimeRef = useRef(0);
  const NOISE_FLOOR = 0.006;

  const getOutputVolume = useCallback((): number => {
    const now = performance.now();
    const dtMs = lastVolumeTimeRef.current ? now - lastVolumeTimeRef.current : 16;
    lastVolumeTimeRef.current = now;

    const provider = ttsRef.current;
    let rawLevel = -1; // -1 means no real audio

    // Try streaming TTS analyser first, then fall back to single-shot ElevenLabs analyser
    const analyser = streamingTtsRef.current?.getAnalyser()
      || (provider && 'getAnalyser' in provider ? (provider as any).getAnalyser() as AnalyserNode | null : null);

    if (analyser) {
      try {
        analyser.getFloatTimeDomainData(timeDomainData.current);
        let sum = 0;
        for (let i = 0; i < timeDomainData.current.length; i++) {
          const x = timeDomainData.current[i];
          sum += x * x;
        }
        rawLevel = Math.sqrt(sum / timeDomainData.current.length);
      } catch {
        // fall through to synthetic
      }
    }

    let curved: number;

    if (rawLevel >= 0) {
      // Real audio: normalize against rolling peak, apply power curve
      peakRef.current = Math.max(rawLevel, peakRef.current * Math.exp(-dtMs / 650));
      const normalized = Math.min(1, Math.max(0,
        (rawLevel - NOISE_FLOOR) / Math.max(peakRef.current - NOISE_FLOOR, 0.001)
      ));
      curved = Math.pow(normalized, 1.5);
    } else if (phaseRef.current === "speaking") {
      // WebSpeech fallback: synthetic speech cadence
      const t = now / 1000;
      const syllables = 0.30 * Math.sin(t * 4.5 + Math.sin(t * 0.7) * 2);
      const breath = 0.20 * Math.sin(t * 1.2 + 1.3);
      const micro = 0.12 * Math.sin(t * 8) * Math.cos(t * 3);
      const chaos = 0.08 * Math.sin(t * 2.3) + 0.05 * Math.sin(t * 3.7);
      curved = Math.max(0, Math.min(1, 0.35 + syllables + breath + micro + chaos));
    } else {
      curved = 0;
    }

    // Attack/release envelope smoothing — fast attack (30ms), slow release (100ms)
    const tau = curved > smoothedVolumeRef.current ? 0.03 : 0.10;
    const alpha = 1 - Math.exp(-(dtMs / 1000) / tau);
    smoothedVolumeRef.current += (curved - smoothedVolumeRef.current) * alpha;

    return smoothedVolumeRef.current;
  }, []);

  // --- Barge-in: detect user speech during TTS playback ---
  const handleDuck = useCallback(() => {
    ttsRef.current?.duck();
    streamingTtsRef.current?.duck();
    setIsDucking(true);
  }, []);

  const handleUnduck = useCallback(() => {
    ttsRef.current?.unduck();
    streamingTtsRef.current?.unduck();
    setIsDucking(false);
  }, []);

  const handleBargeIn = useCallback(() => {
    if (!isActiveRef.current) return;
    console.log("[VoiceMode] Barge-in triggered — stopping TTS, starting listening");

    // Kill stale async continuations
    speakTokenRef.current += 1;
    // Block late callbacks from cancelled turn
    acceptAssistantSpeechRef.current = false;
    // Mark that next send should include interrupt context
    wasInterruptedRef.current = true;

    // Safety net: reopen gate after timeout in case of false barge-in (noise).
    // Primary re-open is in the silence timer when user actually sends.
    if (gateRecoveryTimerRef.current) clearTimeout(gateRecoveryTimerRef.current);
    gateRecoveryTimerRef.current = setTimeout(() => {
      gateRecoveryTimerRef.current = null;
      if (!isActiveRef.current) return;
      // Only reopen if we're still in listening phase (user hasn't sent yet,
      // no new response in flight). If phase is "processing" or "speaking",
      // the gate will be reopened by the normal send path instead.
      if (phaseRef.current !== "listening") return;
      acceptAssistantSpeechRef.current = true;
    }, GATE_RECOVERY_MS);

    clearSilenceTimer();
    sentenceBufferRef.current?.clear();
    sentenceBufferRef.current = null;
    streamingTtsRef.current?.cancelAll();
    ttsRef.current?.stop();
    onAbortAgentRef.current?.(); // Cancel the active SSE stream
    setIsDucking(false);
    setAssistantTranscript("");
    setUserTranscript("");
    setInterimTranscript("");

    // Transition directly to listening
    startListening();
  }, [clearSilenceTimer, startListening]);

  useBargeIn({
    enabled: isActive && phase === "speaking",
    onDuck: handleDuck,
    onUnduck: handleUnduck,
    onBargeIn: handleBargeIn,
  });

  // Explicit user stop (square button while agent is talking). Halts TTS
  // and any in-flight SSE, then drops back to listening. Distinct from
  // barge-in: no "interrupted" flag on the next turn, since this is a
  // deliberate stop rather than the user speaking over us.
  const stopSpeaking = useCallback(() => {
    if (!isActiveRef.current) return;

    speakTokenRef.current += 1;
    acceptAssistantSpeechRef.current = false;
    if (gateRecoveryTimerRef.current) clearTimeout(gateRecoveryTimerRef.current);
    gateRecoveryTimerRef.current = setTimeout(() => {
      gateRecoveryTimerRef.current = null;
      if (!isActiveRef.current) return;
      if (phaseRef.current !== "listening") return;
      acceptAssistantSpeechRef.current = true;
    }, GATE_RECOVERY_MS);

    clearSilenceTimer();
    sentenceBufferRef.current?.clear();
    sentenceBufferRef.current = null;
    streamingTtsRef.current?.cancelAll();
    ttsRef.current?.stop();
    onAbortAgentRef.current?.();
    setIsDucking(false);
    setAssistantTranscript("");
    setUserTranscript("");
    setInterimTranscript("");

    startListening();
  }, [clearSilenceTimer, startListening]);

  // Manual mic mute — orthogonal to phase. Flipping this NEVER touches TTS,
  // SSE, the orb's animation, or anything else about the agent's state. It
  // only gates the user's mic input.
  //
  // - listening + unmuted → mute: stop the SpeechRecognition session and
  //   drop any pending interim/accumulated transcript so re-engaging starts
  //   clean. Phase stays "listening" so the orb keeps its listening look
  //   (matches user request: "mute shouldn't change agent animation").
  // - listening + muted → unmute: restart recognition.
  // - processing/speaking: just flip the flag. Agent is unaffected; the
  //   flag persists into the next listening phase.
  const toggleMic = useCallback(() => {
    if (!isActiveRef.current) return;

    const next = !userMutedRef.current;
    userMutedRef.current = next;
    setUserMuted(next);

    if (phaseRef.current !== "listening") return;

    if (next) {
      stopListening();
      accumulatedTextRef.current = "";
      setUserTranscript("");
      setInterimTranscript("");
    } else {
      startListening();
    }
  }, [stopListening, startListening]);

  // Visibility / focus handler. Native: full exit on background (iOS suspends
  // AudioContext / kills getUserMedia). Desktop: if currently listening and
  // not already muted, set userMuted on tab blur so the mic isn't quietly
  // capturing while the user is away. Never auto-unmutes on return.
  useEffect(() => {
    if (!isActive) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "hidden") return;
      if (isNative()) {
        exit();
        return;
      }
      if (phaseRef.current === "listening" && !userMutedRef.current) {
        userMutedRef.current = true;
        setUserMuted(true);
        stopListening();
        accumulatedTextRef.current = "";
        setUserTranscript("");
        setInterimTranscript("");
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [isActive, exit, stopListening]);

  return {
    isActive,
    phase,
    isMicLive: phase === "listening" && !userMuted,
    userTranscript,
    interimTranscript,
    assistantTranscript,
    isSpeaking: phase === "speaking",
    isDucking,
    enter,
    exit,
    toggleMic,
    speakResponse,
    feedTextChunk,
    finishSpeaking,
    stopSpeaking,
    streamingTtsFailed,
    signalProcessing,
    getOutputVolume,
  };
}
